import { Theme, createTheme } from '@mui/material/styles';
import { EColors as EColorsType, TStyledTheme, getColor as getColorFn } from 'theme';

// Extend MUI theme to include getColor and EColors
declare module '@mui/material/styles' {
    interface Theme {
        getColor: typeof getColorFn;
        EColors: typeof EColorsType;
    }
    interface ThemeOptions {
        getColor?: typeof getColorFn;
        EColors?: typeof EColorsType;
    }
}

// This function creates a MUI v5 compatible theme
export const createMaterialUIThemeV5 = (styledTheme: TStyledTheme): Theme => {
    const { getColor, getFontStyle, EColors, EFonts } = styledTheme;

    return createTheme({
        // Add getColor and EColors to the MUI theme
        getColor,
        EColors,
        palette: {
            primary: {
                main: getColor(EColors.primaryAction),
                contrastText: getColor(EColors.pureWhite),
            },
            secondary: {
                main: getColor(EColors.primaryAction),
            },
            error: {
                main: getColor(EColors.red),
            },
        },
        components: {
            // Per designs with larger text than MUI default
            MuiTableCell: {
                styleOverrides: {
                    body: {
                        fontSize: '15px',
                    },
                    head: {
                        fontSize: '15px',
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    notchedOutline: {
                        borderRadius: '8px',
                    },
                    root: {
                        '&.Mui-focused': {
                            '&& > fieldset': {
                                borderColor: `${getColor(EColors.pureBlack, 0.23)}`,
                                borderWidth: '1px !important',
                                boxShadow: `0 2px 4px 0 ${getColor(EColors.pureBlack, 0.06)}`,
                            },
                        },
                        '&:hover': {
                            '&& > fieldset': {
                                borderColor: `${getColor(EColors.pureBlack, 0.23)}`,
                                boxShadow: `0 2px 4px 0 ${getColor(EColors.pureBlack, 0.06)}`,
                            },
                        },
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    outlinedPrimary: {
                        borderColor: getColor(EColors.primaryAction),
                        '&:hover': {
                            borderColor: getColor(EColors.primaryAction),
                        },
                    },
                },
            },
            MuiSnackbarContent: {
                styleOverrides: {
                    root: {
                        '&[class*="variantInfo"]': {
                            backgroundColor: getColor(EColors.snackbarInfo),
                        },
                        '&[class*="variantError"]': {
                            backgroundColor: getColor(EColors.snackbarError),
                        },
                        '&[class*="variantSuccess"]': {
                            backgroundColor: getColor(EColors.snackbarSuccess),
                        },
                    },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        fontSize: '.85rem',
                    },
                },
            },
            MuiButtonBase: {
                defaultProps: {
                    disableRipple: true,
                },
            },
        },
        typography: {
            button: {
                textTransform: 'none',
            },
            ...getFontStyle(EFonts.body),
        },
    });
};

// Adapter function for styled-components - this replaces materialUIThemeStyledFn
export const materialUIThemeV5Fn = ({ theme }: { theme: TStyledTheme }) => {
    return {
        theme: createMaterialUIThemeV5(theme),
    };
};
